import { useIntl } from 'react-intl';

import { Filename } from '~/eds/src/components/Filename/Filename';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { formatDate } from '~/eds/src/utils/formatDate';
import { parseDate } from '~/eds/src/utils/parseDate';
import { DATE_FORMAT_TYPE_TO_DATE_FORMAT } from '~/enums/DateFormatType';
import { TicketSpecialOptionValueType } from '~/enums/TicketSpecialOptionValueType';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import {
  AddressValue,
  BooleanValue,
  CheckboxRadioCustomSettings,
  CheckboxRadioValue,
  DateCustomSettings,
  FileValue,
  MonetaryValue,
  NumberArrayValue,
  NumberValue,
  ReferenceValue,
  StringArrayValue,
  StringValue,
  TimePeriodCustomSettings,
  TimePeriodValue,
} from '../types/fields';
import { downloadTicketFileAttachment } from '../utils/downloadTicketFileAttachment';
import { getFullIntakeFormFileName } from '../utils/getFullIntakeFormFileName';
import { DepartmentQuestionValue } from './DepartmentQuestionValue';
import { UserQuestionValue } from './UserQuestionValue';

const MULTI_VALUE_DELIMITER = ' • ';

const isEmpty = (value: any): boolean => {
  return value === null || value === undefined || value === '';
};

export const useDisplayQuestionValue = (): ((
  field: any,
) => string | React.ReactNode) => {
  const intl = useIntl();
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  const formatNumber = useFormatNumber();

  const formatAddressValue = (value: AddressValue): string => {
    const { street_1, street_2, city, state, zip_code, country } = value;
    return [street_1, street_2, city, state, zip_code, country]
      .filter(Boolean)
      .join(', ');
  };

  const formatBooleanValue = (value: BooleanValue): string => {
    return value.value
      ? intl.formatMessage({
          id: 'WorkflowForm.displayQuestionValue.yes',
          defaultMessage: 'Yes',
          description:
            'Displays for positive answer on the intake form for yes or no questions',
        })
      : intl.formatMessage({
          id: 'WorkflowForm.displayQuestionValue.no',
          defaultMessage: 'No',
          description:
            'Displays for negative answer on the intake form for yes or no questions',
        });
  };

  const formatCheckboxRadioValue = (
    value: CheckboxRadioValue,
    customSettings: CheckboxRadioCustomSettings,
  ): string | null => {
    if (!value.value) return null;

    const options = customSettings.options || [];
    const formattedValue = options
      .map((option) =>
        value.value!.includes(option.value) ? option.label : null,
      )
      .filter((option) => option !== null)
      .join(MULTI_VALUE_DELIMITER);

    return value.value.includes(TicketSpecialOptionValueType.Other) &&
      value.other_value
      ? `${formattedValue} (${value.other_value})`
      : formattedValue;
  };

  const formatDateValue = (
    value: StringValue,
    customSettings?: DateCustomSettings,
  ): string | undefined | null => {
    return (
      value.value &&
      //l10n-ignore: TECH DEBT - use formatDate to avoid localization issues with ticket injection
      formatDate(
        parseDate(value.value),
        customSettings?.format
          ? DATE_FORMAT_TYPE_TO_DATE_FORMAT[customSettings.format]
          : undefined,
      )
    );
  };

  const formatDepartmentValue = (
    value: NumberArrayValue | NumberValue,
  ): React.ReactNode | null => {
    if (!value.value) return null;

    const departmentIds =
      typeof value.value === 'number' ? [value.value] : value.value;
    return <DepartmentQuestionValue departmentIds={departmentIds} />;
  };

  const formatEmailValue = (value: StringValue): React.ReactNode | null => {
    if (!value.value) return null;

    return <Link href={`mailto:${value.value}`}>{value.value}</Link>;
  };

  const formatFileValue = (value: FileValue): React.ReactNode | null => {
    if (!value.value) return null;

    return (
      <Layout
        as="ul"
        direction="column"
        spacing={1}
        align="start"
        p={0}
        m={0}
        listStyleType="none"
      >
        {value.value?.map((file) => {
          const fileName = getFullIntakeFormFileName(file);
          return (
            <Layout as="li" key={file.id}>
              <Filename
                name={fileName}
                onClick={() => downloadTicketFileAttachment(file, intl)}
              />
            </Layout>
          );
        })}
      </Layout>
    );
  };

  const formatMonetaryValue = (value: MonetaryValue): string | null => {
    if (isEmpty(value.amount)) {
      return null;
    }
    return hasLocalization
      ? formatNumber(value.amount as number, {
          style: 'currency',
          currency: value.currency,
        })
      : //l10n-ignore: we dont want internal l10n logic to localize with eds if the flag is not enabled
        intl.formatNumber(value.amount as number, {
          style: 'currency',
          currency: value.currency,
        });
  };

  const formatMultiSelectValue = (value: StringArrayValue): string | null => {
    if (!value.value) return null;
    return value.value.join(MULTI_VALUE_DELIMITER);
  };

  const formatNumberValue = (
    value: NumberValue | StringValue,
  ): string | null => {
    if (isEmpty(value.value)) return null;
    return formatNumber(value.value as number);
  };

  const formatPercentageValue = (
    value: NumberValue | StringValue,
  ): string | null => {
    if (isEmpty(value.value)) return null;

    const numberValue = hasLocalization
      ? Number(value.value) / 100
      : Number(value.value);

    //l10n ignore: we dont want internal l10n logic to localize with eds if the flag is not enabled
    return hasLocalization
      ? `${formatNumber(numberValue, { style: 'percent' })}`
      : `${numberValue}%`;
  };

  const formatTimePeriodValue = (
    value: TimePeriodValue,
    customSettings: TimePeriodCustomSettings,
  ): string | null => {
    if (isEmpty(value.amount)) return null;

    const { options } = customSettings;
    const period = options.find(
      (option) => option.value === value.period,
    )?.label;
    return `${value.amount} ${period ?? ''}`.trim();
  };

  const formatUserValue = (
    value: NumberArrayValue | NumberValue,
  ): React.ReactNode | null => {
    if (!value.value) return null;

    const userIds =
      typeof value.value === 'number' ? [value.value] : value.value;
    return <UserQuestionValue userIds={userIds} />;
  };

  const formatStringValue = (value: StringValue): string | null => {
    return value.value ?? null;
  };

  const formatReferenceValue = (value: ReferenceValue): string | null => {
    return value.value?.default_value ?? null;
  };

  return (field: any): string | React.ReactNode => {
    const { type, value, customSettings } = field;
    let formattedValue = null;

    switch (type) {
      case WorkflowFieldType.Address:
        formattedValue = formatAddressValue(value);
        break;
      case WorkflowFieldType.Boolean:
        formattedValue = formatBooleanValue(value);
        break;
      case WorkflowFieldType.Checkbox:
      case WorkflowFieldType.Radio:
        formattedValue = formatCheckboxRadioValue(value, customSettings);
        break;
      case WorkflowFieldType.Date:
        formattedValue = formatDateValue(value, customSettings);
        break;
      case WorkflowFieldType.Department:
        formattedValue = formatDepartmentValue(value);
        break;
      case WorkflowFieldType.Email:
        formattedValue = formatEmailValue(value);
        break;
      case WorkflowFieldType.File:
        formattedValue = formatFileValue(value);
        break;
      case WorkflowFieldType.LongResponse:
      case WorkflowFieldType.ShortResponse:
      case WorkflowFieldType.SingleSelect:
        formattedValue = formatStringValue(value);
        break;
      case WorkflowFieldType.Reference:
        formattedValue = formatReferenceValue(value);
        break;
      case WorkflowFieldType.MonetaryValue:
        formattedValue = formatMonetaryValue(value);
        break;
      case WorkflowFieldType.MultiSelect:
        formattedValue = formatMultiSelectValue(value);
        break;
      case WorkflowFieldType.Number:
        formattedValue = formatNumberValue(value);
        break;
      case WorkflowFieldType.Percentage:
        formattedValue = formatPercentageValue(value);
        break;
      case WorkflowFieldType.TimePeriod:
        formattedValue = formatTimePeriodValue(value, customSettings);
        break;
      case WorkflowFieldType.User:
        formattedValue = formatUserValue(value);
        break;
      default:
        formattedValue = value.value?.toString();
        break;
    }

    return formattedValue ?? '-';
  };
};
